// client/src/pages/Dashboard.js
//
// User dashboard — a simple tile-based navigation hub for logged-in users.
//
// Design:
//   - Light + dark mode (matches Contacts.js, MyGifts.js aesthetic)
//   - Pure navigation tiles, no stats or featured CTAs
//   - Trial banner + pending cancellation banner (only when relevant)
//   - Tiles are conditionally rendered based on user.tier:
//       - Business Profile / Analytics:  user?.tier?.canuselinks
//
// Future:
//   - Stat data row will be added at the top later (TBD what data).

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { useSettings } from '../hooks/useSettings.js';
import api from '../services/api.js';

/* ─── Tile (navigation card) ─────────────────────────────────────────
   Matches the bg-royal-900 dark:bg-royal-800/40 pattern used by the
   card sections in Contacts.js / Account.js / MyGifts.js. Light mode
   uses the deep navy treatment (white text on royal-900) for parity
   with those pages. Dark mode keeps the translucent treatment. */
function Tile({ to, icon, label, description, badge }) {
  return (
    <Link
      to={to}
      className="relative block bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-xl p-5 sm:p-6 transition-all hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-md"
    >
      {badge && (
        <span
          className="absolute top-3 right-3 inline-block w-2.5 h-2.5 bg-amber-400 rounded-full ring-2 ring-royal-900"
          aria-hidden="true"
        />
      )}
      <span className="text-3xl block mb-3" aria-hidden="true">{icon}</span>
      <span className="block text-white font-semibold text-base sm:text-lg">{label}</span>
      {description && (
        <span className="block text-royal-100 text-sm mt-1.5 leading-relaxed">{description}</span>
      )}
    </Link>
  );
}

/* ─── Main Dashboard ─────────────────────────────────────────────── */
export default function Dashboard() {
  const { user } = useAuth();
  const { settings } = useSettings();

  const cardsName = settings.cards_display_name || 'Moonbeams';

  const [pendingCancel, setPendingCancel] = useState(false);

  const isBusiness = user?.tier?.canuselinks || false;
  const isPaid = user?.tier?.ispaid || false;

  // Trial calculations
  const isOnTrial = user?.trial_expires_at
    && new Date(user.trial_expires_at) > new Date()
    && !user?.stripecustomerid;
  const trialDaysLeft = isOnTrial
    ? Math.ceil((new Date(user.trial_expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  // Check pending cancellation (same pattern as Navbar)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPaid) { setPendingCancel(false); return; }
      try {
        const data = await api.get('/subscriptions/current');
        if (!cancelled) setPendingCancel(data.subscription?.cancelatperiodend === true);
      } catch {
        // Silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, [isPaid]);

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-6 sm:py-8">

        {/* ─── Page Header ───────────────────────────────── */}
        <div className="mb-6">
          <h1 className="font-display text-2xl sm:text-3xl text-gold-dark dark:text-gold">
            Welcome{user?.firstname ? `, ${user.firstname}` : ''}
          </h1>
          <p className="text-royal-600 dark:text-royal-100 text-sm sm:text-base mt-1">
            Your dashboard — everything in one place.
          </p>
        </div>

        {/* ─── Trial Banner ──────────────────────────────── */}
        {isOnTrial && (
          <div className="mb-5 p-4 bg-royal-900 dark:bg-gold/10 border border-gold/40 dark:border-gold/30 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-gold font-semibold text-base">
                ✨ Free trial — {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left
              </p>
              <p className="text-royal-100 text-sm mt-0.5">
                Subscribe to keep your features after the trial ends.
              </p>
            </div>
            <Link
              to="/pricing"
              className="px-4 py-2 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors whitespace-nowrap text-center"
            >
              View Pricing
            </Link>
          </div>
        )}

        {/* ─── Pending Cancellation Warning ──────────────── */}
        {pendingCancel && (
          <div className="mb-5 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-600/40 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <p className="text-amber-700 dark:text-amber-300 font-semibold text-sm">
                  Subscription ending soon
                </p>
                <p className="text-royal-600 dark:text-royal-200 text-sm mt-0.5">
                  Your subscription is set to cancel at the end of the current period.
                </p>
              </div>
            </div>
            <Link
              to="/account"
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-lg text-sm transition-colors whitespace-nowrap text-center"
            >
              Manage
            </Link>
          </div>
        )}

        {/* ─── Tile Grid ────────────────────────────────────
            All user-facing pages reachable from the navbar dropdown.
            Order matches typical user flow: cards they sent, contacts
            they send to, account/profile management, then business
            features if applicable. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <Tile
            to="/my-moonbeams"
            icon="✉️"
            label={`My ${cardsName}`}
            description="View, edit, or reschedule cards you've created"
          />
          <Tile
            to="/contacts"
            icon="👥"
            label="Contacts"
            description="Add and organize people you send cards to"
          />
          <Tile
            to="/gift/my-gifts"
            icon="🎁"
            label="My Gifts"
            description="Subscription gifts you've sent or received"
          />
          <Tile
            to="/account"
            icon="⚙️"
            label="Account"
            description="Profile, plan, and settings"
            badge={pendingCancel}
          />
          {isBusiness && (
            <Tile
              to="/account/business-profile"
              icon="🏢"
              label="Business Profile"
              description="Branding, sender info, and business details"
            />
          )}
          {isBusiness && (
            <Tile
              to="/account/analytics"
              icon="📊"
              label="Analytics"
              description="See how your cards are performing"
            />
          )}
        </div>

      </div>
    </div>
  );
}